import React, { useState } from "react";
import { Modal, Upload, Table, Button, Alert, message } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { FRANCE, normalizeCountry } from "../../../../modules/helpers/countries";

const COLUMN_MAP = {
  nom: "last_name",
  prenom: "first_name",
  adresse_ligne_1: "address_line_1",
  adresse_ligne_2: "address_line_2",
  code_postal: "postal_code",
  ville: "city",
  pays: "country",
  entreprise: "company_name",
  reference: "reference",
  adresse_retour_nom: "return_address_name",
  adresse_retour_ligne_1: "return_address_line_1",
  adresse_retour_ligne_2: "return_address_line_2",
  adresse_retour_code_postal: "return_address_postal_code",
  adresse_retour_ville: "return_address_city",
};

const REQUIRED_COLUMNS = ["nom", "prenom", "adresse_ligne_1", "code_postal", "ville"];

function validateRow(row, index) {
  const errors = [];
  if (!row.last_name) errors.push("Nom manquant");
  if (!row.first_name) errors.push("Prénom manquant");
  if (!row.address_line_1) errors.push("Adresse manquante");
  if (!row.postal_code) errors.push("Code postal manquant");
  // 5 chiffres pour la France seulement (format libre à l'étranger)
  else if (row.country_code === "FR" && !/^\d{5}$/.test(String(row.postal_code).trim()))
    errors.push("Code postal invalide (5 chiffres)");
  if (!row.city) errors.push("Ville manquante");
  if (!row.country_code) errors.push(`Pays inconnu : « ${row.country} »`);
  return errors;
}

function downloadTemplate() {
  const headers = [
    { nom: "", prenom: "", adresse_ligne_1: "", adresse_ligne_2: "", code_postal: "", ville: "", pays: "", entreprise: "", reference: "", adresse_retour_nom: "", adresse_retour_ligne_1: "", adresse_retour_ligne_2: "", adresse_retour_code_postal: "", adresse_retour_ville: "" },
  ];
  const ws = XLSX.utils.json_to_sheet(headers);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Destinataires");
  XLSX.writeFile(wb, "template_destinataires.xlsx");
}

export default function ExcelImport({ visible, onImport, onCancel }) {
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [hasFile, setHasFile] = useState(false);

  function reset() {
    setParsedData([]);
    setErrors([]);
    setHasFile(false);
  }

  function handleFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // raw : un CSV garde son texte tel quel (« 01000 » reste « 01000 », pas converti en nombre)
        const wb = XLSX.read(e.target.result, { type: "array", raw: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (rawData.length === 0) {
          message.error("Le fichier est vide");
          return;
        }

        // Check required columns
        const fileColumns = Object.keys(rawData[0]).map((c) => c.toLowerCase().trim());
        const missingColumns = REQUIRED_COLUMNS.filter((c) => !fileColumns.includes(c));

        if (missingColumns.length > 0) {
          message.error(`Colonnes manquantes : ${missingColumns.join(", ")}`);
          return;
        }

        // Map columns and validate
        const allErrors = [];
        const mapped = rawData.map((row, index) => {
          const result = { key: `import_${index}` };
          let numericPostalCode = false;
          Object.entries(row).forEach(([col, value]) => {
            const normalizedCol = col.toLowerCase().trim();
            const mappedKey = COLUMN_MAP[normalizedCol];
            if (mappedKey) {
              result[mappedKey] = String(value).trim();
              if (mappedKey === "postal_code" && typeof value === "number") numericPostalCode = true;
            }
          });
          result.type = result.company_name ? "professional" : "individual";
          // Pays normalisé sur la liste de référence (« FR », « FRA » → France ; vide → France) ;
          // inconnu → erreur de ligne (incident EFFIA du 07/10/2026 : « FR » vu comme étranger)
          const country = normalizeCountry(result.country);
          if (country) {
            result.country = country.name;
            result.country_code = country.code;
          }
          // Code postal français à 4 chiffres dans une cellule NUMÉRIQUE : zéro initial perdu par
          // Excel (départements 01 à 09) → complété, et signalé dans l'aperçu pour vérification.
          // Saisi en texte (ou dans un CSV), un code à 4 chiffres reste une erreur : faute de frappe.
          if (numericPostalCode && result.country_code === "FR" && /^\d{4}$/.test(result.postal_code || "")) {
            result.postal_code = `0${result.postal_code}`;
            result._postalCompleted = true;
          }

          const rowErrors = validateRow(result, index);
          if (rowErrors.length > 0) {
            allErrors.push({ row: index + 2, errors: rowErrors });
          }
          result._valid = rowErrors.length === 0;
          return result;
        });

        setParsedData(mapped);
        setErrors(allErrors);
        setHasFile(true);
      } catch (err) {
        message.error("Erreur de lecture du fichier");
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent antd upload
  }

  function handleConfirm() {
    const validRecipients = parsedData
      .filter((r) => r._valid)
      .map(({ _valid, _postalCompleted, key, ...rest }) => ({ ...rest, key }));

    if (validRecipients.length === 0) {
      message.error("Aucun destinataire valide");
      return;
    }

    onImport(validRecipients);
    reset();
  }

  const columns = [
    { title: "Nom", dataIndex: "last_name", key: "last_name" },
    { title: "Prénom", dataIndex: "first_name", key: "first_name" },
    { title: "Adresse", dataIndex: "address_line_1", key: "address_line_1" },
    { title: "CP", dataIndex: "postal_code", key: "postal_code", width: 80 },
    { title: "Ville", dataIndex: "city", key: "city" },
    {
      title: "Pays",
      dataIndex: "country",
      key: "country",
      render: (country, record) =>
        record.country_code ? country : <span style={{ color: "#ff4d4f" }}>{country}</span>,
    },
    { title: "Réf.", dataIndex: "reference", key: "reference" },
    {
      title: "Statut",
      key: "status",
      width: 80,
      render: (_, record) =>
        record._valid ? (
          <span style={{ color: "#52c41a" }}>OK</span>
        ) : (
          <span style={{ color: "#ff4d4f" }}>Erreur</span>
        ),
    },
  ];

  const validCount = parsedData.filter((r) => r._valid).length;
  const invalidCount = parsedData.length - validCount;

  // Répartition par pays des lignes valides : un envoi « à l'étranger » par erreur se voit avant
  // l'import (France en tête)
  const countryCounts = {};
  parsedData
    .filter((r) => r._valid)
    .forEach((r) => {
      countryCounts[r.country] = (countryCounts[r.country] || 0) + 1;
    });
  const countrySummary = Object.entries(countryCounts)
    .sort(([a], [b]) => (a === FRANCE.name ? -1 : b === FRANCE.name ? 1 : a.localeCompare(b, "fr")))
    .map(([name, count]) => `${name} : ${count}`)
    .join(", ");
  const abroadCount = validCount - (countryCounts[FRANCE.name] || 0);
  const completedPostal = parsedData.filter((r) => r._valid && r._postalCompleted);

  return (
    <Modal
      title="Importer des destinataires"
      visible={visible}
      onCancel={() => {
        reset();
        onCancel();
      }}
      width="80%"
      footer={
        hasFile
          ? [
              <Button key="back" onClick={() => { reset(); }}>
                Changer de fichier
              </Button>,
              <Button key="confirm" type="primary" onClick={handleConfirm} disabled={validCount === 0}>
                Importer {validCount} destinataire{validCount > 1 ? "s" : ""}
              </Button>,
            ]
          : null
      }
    >
      {!hasFile ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              Télécharger le template Excel
            </Button>
          </div>
          <Upload.Dragger
            accept=".xlsx,.xls,.csv"
            beforeUpload={handleFile}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Cliquez ou glissez votre fichier ici</p>
            <p className="ant-upload-hint">
              Formats acceptés : Excel (.xlsx, .xls) ou CSV (.csv).
              Colonnes requises : nom, prenom, adresse_ligne_1, code_postal, ville
            </p>
          </Upload.Dragger>
        </>
      ) : (
        <>
          {errors.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${invalidCount} ligne(s) avec erreurs — elles ne seront pas importées`}
              description={errors.slice(0, 5).map((e) => (
                <div key={e.row}>Ligne {e.row} : {e.errors.join(", ")}</div>
              ))}
            />
          )}
          {validCount > 0 && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${validCount} destinataire(s) prêt(s) à importer`}
              description={`Pays : ${countrySummary}`}
            />
          )}
          {completedPostal.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${completedPostal.length} code(s) postal(aux) complété(s) d'un 0 initial (ex. ${completedPostal[0].postal_code.slice(1)} → ${completedPostal[0].postal_code})`}
              description="Excel retire le 0 des codes postaux des départements 01 à 09 quand la colonne n'est pas au format texte. Vérifiez-les si besoin."
            />
          )}
          {abroadCount > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${abroadCount} destinataire(s) hors de France`}
              description="Vérifiez la colonne « pays » si ce n'est pas voulu : un envoi à l'étranger ne suit pas le circuit des recommandés nationaux."
            />
          )}
          <Table
            dataSource={parsedData}
            columns={columns}
            pagination={parsedData.length > 20 ? { pageSize: 20 } : false}
            size="small"
            rowClassName={(record) => (record._valid ? "" : "ant-table-row-error")}
          />
        </>
      )}
    </Modal>
  );
}
